import * as React from "react";
import {
  Box,
  Checkbox,
  CheckboxGroup,
  Column,
  Columns,
  Divider,
  Stack,
  Text,
} from "@origin-digital/ods-core";
import {
  Field,
  FieldInputProps,
  FieldProps,
  FormikErrors,
  FormikProps,
  useFormikContext,
} from "formik";
import { AccountPayer, FormValues } from "../types";
import FormContext, { GasService } from "src/context/FormContext";
import { useMemo } from "react";

interface IServiceTypesProps {
  error:
    | string
    | FormikErrors<any>
    | FormikErrors<any>[]
    | string[]
    | undefined;
  isEditing: boolean;
}

const ServiceTypes = (props: IServiceTypesProps) => {
  const internetIsNotHidden = false;
  const { error, isEditing } = props;
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const {
    formState: { electricityService, gasServices },
  } = React.useContext(FormContext);

  const gasServicesValues: GasService[] = gasServices
    ? Object.values(gasServices)
    : [];

  const isElectricityDisabled = Boolean(
    (electricityService?.draftId && isEditing) ||
      electricityService?.channel === "CLAIM" ||
      electricityService?.isReadOnly
  );

  const accountPayers = useMemo(
    () => [
      values.electricityAccountPayer,
      values.gasLine?.accountPayer,
      values.gasMeter?.accountPayer,
      values.both?.accountPayer,
    ],
    [
      values.electricityAccountPayer,
      values.gasLine?.accountPayer,
      values.gasMeter?.accountPayer,
      values.both?.accountPayer,
    ]
  );

  const isGasDisabled = Boolean(
    isEditing &&
      gasServicesValues.length > 0 &&
      gasServicesValues.some((gasService: GasService) => {
        return gasService.draftId || gasService.isReadOnly;
      })
  );

  const showReadOnly = isElectricityDisabled && isGasDisabled && isEditing;

  return showReadOnly ? (
    <ReadOnlyServiceTypes selectedConnections={values.selectedConnections} />
  ) : (
    <CheckboxGroup
      id="connection-type"
      label="What’s being connected?"
      //@ts-expect-error: linting does not allow for
      error={error}
      helperText={error as string}
      //@ts-expect-error: auto blur to make TextLint in help text clickable
      onClick={(e) => e.target.blur()}
    >
      <Box
        display="flex"
        flexWrap="wrap"
        flexDirection={{
          xs: "column",
          sm: "column",
        }}
      >
        <Field type="checkbox" name="selectedConnections" value="Electricity">
          {({ field }: { field: FormikProps<FieldProps> }) => (
            <Checkbox
              {...field}
              id="electricity"
              label="Electricity"
              value="Electricity"
              disabled={isElectricityDisabled}
            />
          )}
        </Field>
        <Field type="checkbox" name="selectedConnections" value="Gas">
          {({ field }: { field: FieldInputProps<boolean> }) => (
            <Checkbox
              {...field}
              id="natural-gas"
              label="Natural Gas"
              value="Gas"
              onChange={(e) => {
                field.onChange(e);
                //special case for ACT as no gas options are displayed for user to select
                if (
                  accountPayers.includes(AccountPayer.SOMEONE_ELSE) &&
                  values.premiseAddress.state === "ACT"
                ) {
                  values.gasOptions.forEach((option) => {
                    setFieldValue(
                      `${option}.accountPayer`,
                      AccountPayer.SOMEONE_ELSE
                    );
                  });
                }
              }}
              disabled={isGasDisabled}
            />
          )}
        </Field>
        {internetIsNotHidden ? (
          <Field type="checkbox" name="selectedConnections" value="Internet">
            {({ field }: { field: FormikProps<FieldProps> }) => (
              <Checkbox
                {...field}
                id="internet"
                label="Internet"
                value="Internet"
              />
            )}
          </Field>
        ) : null}
      </Box>
    </CheckboxGroup>
  );
};

interface ReadOnlyServiceTypesProps {
  selectedConnections: string[];
}

export const ReadOnlyServiceTypes: React.FC<ReadOnlyServiceTypesProps> = ({
  selectedConnections,
}) => {
  return (
    <Columns
      collapseBelow="md"
      space={{
        xs: "xsmall",
        md: "xxlarge",
      }}
      alignY="stretch"
    >
      <Column width="2/3">
        {/* What’s being connected? */}
        <Stack space="xsmall">
          <Stack space="xxsmall">
            <Text variant="body-small" tone="neutralLight">
              What’s being connected?
            </Text>
            <Text data-role="value" data-id="selectedConnectionsReadOnly">
              {selectedConnections.join(", ")}
            </Text>
          </Stack>
          <Divider />
        </Stack>
        {/* End What’s being connected? */}
      </Column>
      {/* HelperText */}
      <Column width="1/3" />
      {/* End HelperText */}
    </Columns>
  );
};

export default ServiceTypes;
